import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getErrorMessage } from '../lib/errors';
import { useAuthStore } from '../stores/authStore';
import CardNB from '../components/ui-nb/CardNB';
import IconNB from '../components/ui-nb/IconNB';
import { debug, error as logError, info } from '../lib/logger';

export default function AuthCallback() {
  const navigate = useNavigate();
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { signIn } = useAuthStore();

  useEffect(() => {
    const handleAuth = async () => {
      try {
        info('Auth callback - checking URL');

        // Check for error in URL
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const searchParams = new URLSearchParams(window.location.search);

        const errorCode = searchParams.get('error') || hashParams.get('error');
        const errorDesc = searchParams.get('error_description') || hashParams.get('error_description');

        if (errorCode) {
          logError('OAuth error', { errorCode, errorDesc });
          throw new Error(errorDesc || errorCode || 'Authentication failed');
        }

        // Log current URL for debugging
        debug('Current URL', { href: window.location.href });
        debug('Hash', { hash: window.location.hash });

        // For hash-based OAuth (Supabase default)
        if (window.location.hash.includes('access_token')) {
          debug('Found access token in hash');
        }

        // Get session - Supabase handles token exchange automatically
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          logError('Session error', { error: getErrorMessage(sessionError) });
          throw sessionError;
        }

        debug('Session', { status: session ? 'exists' : 'none' });

        if (session) {
          // Get user profile to check if org is set
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          debug('Profile', { profile });

          // Update auth store
          signIn({
            id: session.user.id,
            email: session.user.email || '',
            name: session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || '',
            role: profile?.role || 'technician',
            organizationId: profile?.organization_id || null,
            organizationName: null,
            provider: (session.user.app_metadata?.provider as 'google' | 'email') || 'email',
          });

          // Check for pending invite code after login
          // If pending code exists, redirect to join page instead of setup
          const pendingInviteCode = localStorage.getItem('tellowup-pending-invite-code');
          const lang = params.lang || 'en';

          if (pendingInviteCode) {
            localStorage.removeItem('tellowup-pending-invite-code');
            navigate(`/${lang}/join/${pendingInviteCode}`);
            return;
          }

          // Check if user has organization set up
          if (profile?.organization_id) {
            const rolePath = profile?.role === 'admin' ? 'admin' : 'technician';
            navigate(`/${lang}/${rolePath}`);
          } else {
            navigate(`/${lang}/setup/role`);
          }
        } else {
          // No session - try to exchange the code
          debug('No session, attempting token exchange');

          // If there's a hash with tokens, try to get session again after a moment
          if (window.location.hash) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            const { data: { session: retrySession } } = await supabase.auth.getSession();

            if (retrySession) {
              const lang = params.lang || 'en';
              navigate(`/${lang}/setup/role`);
              return;
            }
          }

          const lang = params.lang || 'en';
          navigate(`/${lang}/login`);
        }
      } catch (err) {
        logError('Auth callback error', { error: getErrorMessage(err) });
        setError(getErrorMessage(err) || 'Authentication failed');
      } finally {
        setLoading(false);
      }
    };

    handleAuth();
  }, [navigate, params.lang]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-nb-cream">
        <CardNB className="text-center max-w-md mx-auto p-8">
          <div className="w-12 h-12 border-3 border-nb-black border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-nb-text-secondary">Signing you in...</p>
        </CardNB>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-nb-cream">
        <CardNB className="text-center max-w-md mx-auto p-8">
          <h2
            className="text-2xl font-bold text-black mb-4">
            Authentication Error
          </h2>
          <p className="mb-4 text-nb-text-secondary">{error}</p>
          <a href="/en/login" className="hover:underline text-nb-black">Try again</a>
        </CardNB>
      </div>
    );
  }

  return null;
}
